/* CtxStringsService.js */
import { DEFAULT_LOCALE } from './common/Constants';
/**
 * Compare two strings according to locale.
 *
 * @param source Source string.
 * @param target Target string.
 * @param locale Specified locale.
 * @returns      A negative number if the source string occurs before the target string;
 *               positive if the source string occurs after the target string; 0 if they are equivalent.
 *               Note: Strings that differ in base letters, accents and other diacritic marks,
 *               or case compare as unequal.
 */

function localizedCompare(source, target, locale) {
  var localeValue = locale || DEFAULT_LOCALE;

  if (source === null && target === null || source === undefined && target === undefined) {
    return 0;
  }

  if ((source === null || source === undefined) && target !== null && target !== undefined) {
    return 1;
  }

  return source.localeCompare(target, localeValue);
}
/**
 * Search target string in the source string ignoring the character case in specified locale.
 * @param source Source string.
 * @param target Target string.
 * @param locale Specified locale.
 * @returns      Index value of the first character of target string in source string.
 *               Return -1 if target string not found.
 * @throws {RangeError | TypeError} Incorrect locale information provided, @see {@link String#toLocaleLowerCase}
 */


function caseInsenstiveSearchWithLocale(source, target) {
  var locale = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : DEFAULT_LOCALE;
  return source.toLocaleLowerCase(locale).indexOf(target.toLocaleLowerCase(locale));
}
/**
 * Get an upper case string using the locale.
 *
 * @param source Target string
 * @param locale Specified locale
 * @returns      String in upper case
 */


function upperCaseWithLocale(source, locale) {
  var localeValue = locale || DEFAULT_LOCALE;

  try {
    return source.toLocaleUpperCase(localeValue);
  } catch (error) {
    return source.toLocaleUpperCase(DEFAULT_LOCALE);
  }
}
/**
 * Get a lower case string using the locale.
 *
 * @param source Target string
 * @param locale Specified locale
 * @returns      String in lower case
 */


function lowerCaseWithLocale(source, locale) {
  var localeValue = locale || DEFAULT_LOCALE;

  try {
    return source.toLocaleLowerCase(localeValue);
  } catch (error) {
    return source.toLocaleLowerCase(DEFAULT_LOCALE);
  }
}
/**
 * check if the string conatins specific character
 * @param str Target string
 * @param checkFunction function to check character
 * @returns True if all character in the string are specific characters. Otherwise false.
 */


function checkStringInternal(str, checkFunction) {
  return str.length != 0 && !str.split('').some(function (e) {
    return !checkFunction(e);
  });
}
/**
 * Check if a string contains non-ASCII characters.
 *
 * @param str Target string.
 * @returns   True if all characters in the string are ASCII characters. Otherwise false.
 */


function isASCIIString(str) {
  return checkStringInternal(str, isASCIICharacter);
}
/**
 * Check if a string contains non-Latin characters.
 * @param str Target string.
 * @returns   True if all characters in the string are Latin characters. Otherwise false.
 */


function isLatinString(str) {
  return checkStringInternal(str, isLatinCharacter);
}
/**
 * Check if a string contains non-Danish characters.
 * @param str Target string.
 * @returns   True if all characters in the string are Danish characters. Otherwise false.
 */


function isDanishString(str) {
  return checkStringInternal(str, isDanishCharacter);
}
/**
 * Check if a string contains non-Cyrillic characters.
 * @param str Target string.
 * @returns   True if all characters in the string are Cyrillic characters. Otherwise false.
 */


function isCyrillicString(str) {
  return checkStringInternal(str, isCyrillicCharacter);
}
/**
 * Check if a character is an ASCII character.
 * @param str Target character.
 * @returns   True if the target character is an ASCII character. Otherwise false.
 */


function isASCIICharacter(str) {
  if (str.length == 1) {
    return str[0] > "\0" && str[0] < "\x7F";
  }

  return false;
}
/**
 * Check if a character is a Latin character.
 * @param str Target character.
 * @returns   True if the target character is Latin character. Otherwise false.
 */


function isLatinCharacter(str) {
  if (str.length == 1) {
    var regexLatinSymbol = /(?:[A-Za-z\xAA\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02B8\u02E0-\u02E4\u1D00-\u1D25\u1D2C-\u1D5C\u1D62-\u1D65\u1D6B-\u1D77\u1D79-\u1DBE\u1E00-\u1EFF\u2071\u207F\u2090-\u209C\u212A\u212B\u2132\u214E\u2160-\u2188\u2C60-\u2C7F\uA722-\uA787\uA78B-\uA7CA\uA7D0\uA7D1\uA7D3\uA7D5-\uA7D9\uA7F2-\uA7FF\uAB30-\uAB5A\uAB5C-\uAB64\uAB66-\uAB69\uFB00-\uFB06\uFF21-\uFF3A\uFF41-\uFF5A]|\uD801[\uDF80-\uDF85\uDF87-\uDFB0\uDFB2-\uDFBA]|\uD837[\uDF00-\uDF1E\uDF25-\uDF2A])/;
    return regexLatinSymbol.test(str);
  }

  return false;
}
/**
 * Check if a character is a Danish character.
 *
 *  Note: Latin letters add below character.
 *  Unicode  Upper  Unicode  Lower  Character Name
 *   00C6     Æ      00E6     æ          AE
 *   00C5     Å      00E5     å       A ring above
 *   00D8     Ø      00F8     ø        O stroke
 * @param str Target character.
 * @returns   True if the target character is a Danish character. Otherwise false.
 */


function isDanishCharacter(str) {
  if (str.length == 1) {
    var testChar = str[0];
    return isLatinCharacter(str) || testChar == "\xC6" || testChar == "\xE6" || testChar == "\xC5" || testChar == "\xE5" || testChar == "\xD8" || testChar == "\xF8";
  }

  return false;
}
/**
 * Check if a character is a Cyrillic character.
 *
 * @param str Target character.
 * @returns   True if the target character is a Cyrillic character. Otherwise false.
 */


function isCyrillicCharacter(str) {
  if (str.length == 1) {
    var regexCyrillicSymbol = /(?:[\u0400-\u0484\u0487-\u052F\u1C80-\u1C88\u1D2B\u1D78\u2DE0-\u2DFF\uA640-\uA69F\uFE2E\uFE2F]|\uD838[\uDC30-\uDC6D\uDC8F])/;
    return regexCyrillicSymbol.test(str);
  }

  return false;
}
/**
 * Returns a new string consisting of the character located at the specified index of the string
 *
 * @param str The string which can contains all uniocde character.
 * @param index The zero-based index of the desired character.
 * @returns  Unicode string.
 *           For example, charAt('a𝐀b𝐁', 1) returns '𝐀', while the system API 'a𝐀b𝐁'.charAt(1) returns '\uD835', it is the the first surrogate half for '𝐀',
 *           JavaScript uses two bytes to represent a character, '𝐀' needs four bytes in JavaScript encoding system, so the system charAt return the first two bytes
 *           code unit, which will cause bug if you want to dispaly this return value.
 */


function charAt(str, index) {
  if (!str) {
    return '';
  }

  var arrayStr = Array.from(str);

  if (index >= arrayStr.length || index < 0) {
    return '';
  }

  return arrayStr[index];
}
/**
 * Returns the length of string
 *
 * @param str The string which can contains all uniocde character, for example 'a😀𝐀𝐁'.
 * @returns  The length of string.
 *           For example stringLength('a😀𝐀𝐁') returns 4, 'a😀𝐀𝐁'.length is 7.
 *           If the code point of character is bigger than 65535, JavaScript will use four bytes to represent this character,
 *           and the length of this character will be two, it is confusing.
 */


function stringLength(str) {
  if (!str) {
    return 0;
  }

  var arrayStr = Array.from(str);
  return arrayStr.length;
}
/**
 * Returns the substring at the specified location within a String object.
 *
 * @param str The string which can contains all uniocde character.
 * @param start The zero-based index number indicating the beginning of the substring.
 * @param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.
 *            If end is omitted, the characters from start through the end of the original string are returned.
 * @returns  sub string.
 *           For example, subString('a𝐀𝐁😀𠩤', 1, 4) returns '𝐀😀𝐁',
 *           while the system API 'a𝐀😀𝐁𠩤'.substring(1, 4) returns '𝐀\uD83D', '\uD83D' is the the first surrogate half for '😀', this result is confusing.
 *           JavaScript uses two bytes to represent a character, '𝐀','😀', '𝐁' and '𠩤' all needs four bytes in JavaScript encoding system.
 *           substring(1, 4) will return 3 characters, a total of six bytes, of which '𝐀' occupies 4 bytes, and the other two bytes are taken from '😀'.
 */


function subString(str, start, end) {
  if (!str) {
    return '';
  }

  var arrayStr = Array.from(str);

  if (start === undefined || start === null || start < 0) {
    start = 0;
  }

  if (end === undefined) {
    end = arrayStr.length;
  }

  if (end === null || end < 0) {
    end = 0;
  }

  if (start > end) {
    return arrayStr.slice(end, start).join('');
  }

  return arrayStr.slice(start, end).join('');
}

export { localizedCompare, upperCaseWithLocale, lowerCaseWithLocale, isASCIIString, caseInsenstiveSearchWithLocale, isLatinString, isDanishString, isCyrillicString, isASCIICharacter, isLatinCharacter, isDanishCharacter, isCyrillicCharacter, charAt, stringLength, subString };